import { getIndentUnit, syntaxTree } from "@codemirror/language";
import {
  type EditorState,
  type Extension,
  type Range,
  RangeSet,
  StateEffect,
  StateField,
  type Text,
  type TransactionSpec,
} from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import { type SyntaxNode, type Tree } from "@lezer/common";
import katex from "katex";

import { workspace } from "@/components/workspace/controller";
import { parseRoute } from "@/lib/url-state";
import { openWikiLink, openWikiLinkInBackground } from "@/lib/wikilink";

export { renderCellInline } from "./inline-render";

import { createEditableTable, type EditableTableHandle } from "@/components/reader/editable-table";

import { autoAlignTables } from "./table-edit";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

/** Obsidian-style "Live Preview": walk the markdown syntax tree and decorate it
 *  so markup renders inline as you type. Inline spans get a CSS class; the
 *  literal markers (`**`, `#`, `>`, `==`, `[[ ]]`, `$`) are hidden with atomic
 *  replace decorations EXCEPT on the node the selection is touching, which
 *  reveals the raw markers for editing. The document itself is never altered —
 *  `view.state.doc.toString()` stays byte-identical to what we save. */

/** True when any selection range overlaps `[from, to]` (inclusive of the marker
 *  edges), i.e. the caret is on this node and its markup should be revealed. */
export function selectionTouches(state: EditorState, from: number, to: number): boolean {
  for (const r of state.selection.ranges) {
    if (r.from <= to && r.to >= from) return true;
  }
  return false;
}

/** The reveal test used by every branch below: markup only goes raw when the
 *  editor actually HAS focus *and* the selection touches the node. Without the
 *  focus half, CodeMirror's default selection (an empty range at position 0)
 *  "touches" whatever block opens the document, so a freshly opened note would
 *  greet the reader with a raw `# ` or a table's raw pipes before they had
 *  clicked anything. */
function revealsAt(state: EditorState, focused: boolean, from: number, to: number): boolean {
  return focused && selectionTouches(state, from, to);
}

/** Inline emphasis-like nodes whose delimiter children we hide. */
const INLINE_CLASS: Record<string, string> = {
  StrongEmphasis: "cm-strong",
  Emphasis: "cm-em",
  Strikethrough: "cm-strike",
  InlineCode: "cm-code",
  Highlight: "cm-highlight",
};

/** How many lists of `kind` enclose this node — 0 for a top-level item, 1 for
 *  one nested inside it, and so on without limit. Walking the tree (rather than
 *  dividing the leading whitespace by the indent unit) is what makes the depth
 *  correct for lists whose indentation is irregular, e.g. the three-space indent
 *  an ordered parent forces on its children. */
function depthOf(node: SyntaxNode, kind: string): number {
  let depth = -1; // the item's own enclosing list is level 0
  for (let n: SyntaxNode | null = node; n; n = n.parent) {
    if (n.name === kind) depth++;
  }
  return Math.max(0, depth);
}

export function listDepth(node: SyntaxNode): number {
  return depthOf(node, "BulletList");
}

/** Lower-case roman numeral for 1…3999; the input is already clamped by the
 *  marker regex, and anything out of range falls back to the plain digits. */
export function roman(n: number): string {
  if (!(n >= 1 && n < 4000)) return String(n);
  const table: [number, string][] = [
    [1000, "m"], [900, "cm"], [500, "d"], [400, "cd"], [100, "c"], [90, "xc"],
    [50, "l"], [40, "xl"], [10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"],
  ];
  let out = "";
  let left = n;
  for (const [value, sign] of table) {
    while (left >= value) {
      out += sign;
      left -= value;
    }
  }
  return out;
}

/** Spreadsheet-style letters: 1→a … 26→z, 27→aa. */
export function alpha(n: number): string {
  let out = "";
  let left = n;
  while (left > 0) {
    const rem = (left - 1) % 26;
    out = String.fromCharCode(97 + rem) + out;
    left = Math.floor((left - 1) / 26);
  }
  return out || String(n);
}

/** The marker an ordered item shows at `depth`, cycling every three levels so
 *  nesting stays legible however deep it goes: `1)` → `i)` → `a)`. The NUMBER
 *  still comes from the document (so a list starting at 3 still reads 3) — only
 *  its notation is derived, and the source text is never touched. */
export function orderedLabel(n: number, depth: number, delimiter: string): string {
  const step = depth % 3;
  const body = step === 0 ? String(n) : step === 1 ? roman(n) : alpha(n);
  return `${body}${delimiter}`;
}

class OrderedMarkWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }
  eq(o: OrderedMarkWidget) {
    return o.text === this.text;
  }
  ignoreEvent() {
    return false;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-ordered-mark";
    s.textContent = this.text;
    return s;
  }
}

const HEADING_RE = /^ATXHeading([1-6])$/;
// Backend citations: [node:id] and the restyled variants models sometimes emit.
const CITATION_RE = /[[【(]node:([0-9a-f]{6,})[\]】)]/g;

/** Bullet glyphs by nesting depth, cycling every three levels — the same
 *  disc/circle/square progression a browser gives a plain nested `<ul>`, which
 *  Tailwind's `list-disc` had flattened to one glyph at every level. Indentation
 *  alone does not read as hierarchy once a list is more than a screen tall; the
 *  glyph is what tells you which level you are on. Cycling (rather than running
 *  out) is what lets nesting go arbitrarily deep. */
const BULLET_GLYPHS = ["•", "◦", "▪"];

class BulletWidget extends WidgetType {
  constructor(readonly depth: number) {
    super();
  }
  eq(o: BulletWidget) {
    return o.depth === this.depth;
  }
  ignoreEvent() {
    return false;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-bullet";
    s.textContent = BULLET_GLYPHS[this.depth % BULLET_GLYPHS.length] ?? "•";
    return s;
  }
}

class MathWidget extends WidgetType {
  constructor(readonly tex: string) {
    super();
  }
  eq(o: MathWidget) {
    return o.tex === this.tex;
  }
  ignoreEvent() {
    return false;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-math";
    try {
      s.innerHTML = katex.renderToString(this.tex, { throwOnError: false });
    } catch {
      s.textContent = `$${this.tex}$`;
    }
    return s;
  }
}

class ImageWidget extends WidgetType {
  constructor(
    readonly url: string,
    readonly alt: string,
    /** The image is the ONLY thing on its line, so it reads as a figure and is
     *  centred. An image sitting in a sentence, a list item or a quote stays
     *  inline instead — centring those broke the line in half and left the
     *  text stranded above a floating picture. */
    readonly figure: boolean,
  ) {
    super();
  }
  eq(o: ImageWidget) {
    return o.url === this.url && o.alt === this.alt && o.figure === this.figure;
  }
  ignoreEvent() {
    return false;
  }
  toDOM(view: EditorView) {
    // A stable wrapper (never swapped out) so an error handler can fall back
    // to a small text badge in place without detaching the widget's root node
    // from CodeMirror's view of it.
    const wrap = document.createElement("span");
    wrap.className = this.figure ? "cm-img cm-img--figure" : "cm-img";
    const img = document.createElement("img");
    img.src = this.url;
    img.alt = this.alt;
    // An image arrives with no intrinsic size and then suddenly has one, which
    // is what makes writing around a picture feel like the page is bucking:
    // CodeMirror has already cached a height for that line, the browser reflows
    // underneath it, and every line below jumps while the caret stays put.
    // Asking for a measurement pass the moment the bitmap lands lets the view
    // re-derive its heights in the SAME frame as the reflow, so the document
    // settles once instead of twitching. `decoding="async"` and lazy loading
    // keep that off the typing path.
    img.decoding = "async";
    img.loading = "lazy";
    const settle = () => view.requestMeasure();
    img.addEventListener("load", settle);
    img.onerror = () => {
      img.removeEventListener("load", settle);
      img.remove();
      wrap.classList.add("cm-img--broken");
      wrap.textContent = `🖼 ${this.alt || this.url}`;
      view.requestMeasure();
    };
    wrap.appendChild(img);
    return wrap;
  }
}

class HrWidget extends WidgetType {
  eq() {
    return true;
  }
  toDOM() {
    const hr = document.createElement("hr");
    hr.className = "cm-hr";
    return hr;
  }
}

class CitationWidget extends WidgetType {
  constructor(readonly id: string) {
    super();
  }
  eq(o: CitationWidget) {
    return o.id === this.id;
  }
  toDOM() {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "citation-chip";
    b.title = "Show in atlas";
    b.textContent = `§${this.id.slice(0, 4)}`;
    // mousedown (not click) so the editor doesn't first move the caret here.
    b.addEventListener("mousedown", (e) => {
      e.preventDefault();
      useTabs.getState().activate("graph");
      useGraph.getState().requestFocus(this.id);
    });
    return b;
  }
}

/** Flip `[ ]` ⇄ `[x]` at `pos`. Takes the `{state, dispatch}` shape (an
 *  `EditorView` satisfies it structurally) so it stays unit-testable.
 *
 *  The widget captured `pos` when it was BUILT, so re-read the document there
 *  before writing: an edit somewhere else — or a reload swapping the whole doc
 *  under a still-mounted widget — can leave a click pointing at a position that
 *  no longer holds a checkbox, and overwriting three arbitrary characters would
 *  be a silent corruption. Returns whether it wrote. */
export function toggleTask(
  target: { state: EditorState; dispatch: (spec: TransactionSpec) => void },
  pos: number,
): boolean {
  const cur = target.state.doc.sliceString(pos, pos + 3);
  if (!/^\[[ xX]\]$/.test(cur)) return false;
  target.dispatch({ changes: { from: pos, to: pos + 3, insert: cur === "[ ]" ? "[x]" : "[ ]" } });
  return true;
}

class TaskWidget extends WidgetType {
  constructor(
    readonly checked: boolean,
    /** Document position of the `[` — where `toggleTask` writes. */
    readonly pos: number,
  ) {
    super();
  }
  eq(o: TaskWidget) {
    return o.checked === this.checked && o.pos === this.pos;
  }
  // No `ignoreEvent` override: the default (true) hands events to the widget
  // and keeps CodeMirror out of them, which is what an interactive control
  // wants — same as CitationWidget.
  toDOM(view: EditorView) {
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "cm-task";
    box.checked = this.checked;
    box.setAttribute("aria-label", this.checked ? "Mark task incomplete" : "Mark task complete");
    // mousedown, not click, for the suppression: letting the editor handle it
    // would focus the surface and drop the caret on this line, which reveals
    // the raw `- [x] ` markup and yanks the box out from under the pointer.
    box.addEventListener("mousedown", (e) => e.preventDefault());
    box.addEventListener("click", (e) => {
      // The document is the single source of truth for `checked`; suppress the
      // browser's own toggle and let the re-rendered widget show the result.
      e.preventDefault();
      toggleTask(view, this.pos);
    });
    return box;
  }
}

export type TableAlign = "left" | "center" | "right" | null;
export interface TableModel {
  head: string[];
  /** Per-column `text-align` from the delimiter row's colons, `null` if unset. */
  align: TableAlign[];
  body: string[][];
}

/** Split one GFM table row into trimmed cell texts. `\|` is content, and the
 *  optional leading/trailing pipe is a fence, not an empty cell. */
function splitTableRow(text: string): string[] {
  const t = text.trim();
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (ch === "\\" && t[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "|") {
      cells.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur);
  if (t.startsWith("|")) cells.shift();
  if (cells.length && /(^|[^\\])\|$/.test(t)) cells.pop();
  return cells.map((c) => c.trim());
}

const DELIMITER_CELL = /^:?-+:?$/;

function alignOf(spec: string): TableAlign {
  const left = spec.startsWith(":");
  const right = spec.endsWith(":");
  return left && right ? "center" : right ? "right" : left ? "left" : null;
}

/** Parse a table straight out of its source lines. Lezer gives us `TableCell`
 *  nodes with inline markup already parsed, but the widget compiles each cell's
 *  markup itself (`renderCellInline`), so a textual pipe split stays both
 *  lighter and easier to test than walking the subtree. Returns null if the
 *  delimiter row doesn't hold up. */
export function parseTable(source: string): TableModel | null {
  const lines = source.split("\n");
  if (lines.length < 2) return null;
  const head = splitTableRow(lines[0] ?? "");
  const delimiters = splitTableRow(lines[1] ?? "");
  if (!head.length || !delimiters.length) return null;
  if (!delimiters.every((c) => DELIMITER_CELL.test(c))) return null;
  return {
    head,
    align: head.map((_, i) => alignOf(delimiters[i] ?? "")),
    body: lines.slice(2).map(splitTableRow),
  };
}


/** What a `Table` node actually becomes on screen, or null when it can't be
 *  rendered at all. Both layers ask: `collectBlocks` to build the widget,
 *  `collect` to decide whether skipping the subtree is safe.
 *
 *  Two things live here rather than at the call sites because the layers have
 *  to agree exactly:
 *   - the range is whole LINES, not the node's own bounds. A table indented
 *     inside a list item starts a couple of columns in, and the reveal test has
 *     to run against the range we actually replace — otherwise the caret can
 *     park in the gap between the two (inside the widget, yet not "touching"
 *     the node), where the table never reverts and typing corrupts the block.
 *   - `null` is a real answer: a table inside a blockquote keeps a `> ` on
 *     every row, which `parseTable` rightly rejects. `collect` must then NOT
 *     skip the subtree, or the quoted rows lose their inline decoration too and
 *     render as raw `| **x** |`. */
interface TableBlock {
  from: number;
  to: number;
  source: string;
  model: TableModel;
}

function tableBlock(doc: Text, node: SyntaxNode): TableBlock | null {
  const first = doc.lineAt(node.from);
  const last = doc.lineAt(node.to);
  const source = doc.sliceString(first.from, last.to);
  const model = parseTable(source);
  return model ? { from: first.from, to: last.to, source, model } : null;
}

class TableWidget extends WidgetType {
  constructor(
    /** The raw source the model came from — a cheap, exact identity for `eq`. */
    readonly source: string,
    readonly model: TableModel,
    /** Document offset of the table's first character: where an edit made in
     *  the grid gets written back to. */
    readonly from: number,
  ) {
    super();
  }
  eq(o: TableWidget) {
    // Deliberately IGNORES `source`. A widget that compared its text would be
    // torn down and rebuilt on every committed cell edit, destroying the very
    // element the user is working in; `updateDOM` re-fills the cells instead.
    return o.from === this.from;
  }
  ignoreEvent() {
    // Everything inside belongs to the grid: its cells are contenteditable and
    // handle their own typing, focus and Tab traversal.
    return true;
  }
  ignoreMutation() {
    // CRITICAL for an editable widget. CodeMirror's DOM observer otherwise
    // treats typing inside these cells as the user editing the EDITOR's own
    // text and tries to reconcile it into the document — which mangled the note
    // rather than the cell. The grid owns its DOM; edits reach the document
    // only through the explicit dispatch in `toDOM`.
    return true;
  }
  updateDOM(dom: HTMLElement) {
    const handle = HANDLES.get(dom);
    if (!handle) return false;
    handle.sync(this.source);
    return true;
  }
  toDOM(view: EditorView) {
    const built = createEditableTable(this.source, (next) => {
      // Write the regenerated markdown over exactly the range this widget
      // replaced. `from` is stable for the life of the widget because `eq`
      // keys on it, and a committed edit only ever rewrites the table itself.
      const to = Math.min(this.from + this.source.length, view.state.doc.length);
      if (view.state.sliceDoc(this.from, to) !== this.source) return; // moved — skip
      view.dispatch({ changes: { from: this.from, to, insert: next }, userEvent: "input" });
    });
    if (!built) {
      // Unrenderable shape — fall back to the plain text so nothing disappears.
      const pre = document.createElement("div");
      pre.textContent = this.source;
      return pre;
    }
    HANDLES.set(built.dom, built);
    return built.dom;
  }
}

/** Widget DOM → its grid handle, so `updateDOM` can refresh cells in place
 *  rather than rebuilding (which would drop focus mid-edit). A WeakMap so a
 *  detached table is collected with its handle. */
const HANDLES = new WeakMap<HTMLElement, EditableTableHandle>();

class CodeLangWidget extends WidgetType {
  constructor(readonly lang: string) {
    super();
  }
  eq(o: CodeLangWidget) {
    return o.lang === this.lang;
  }
  ignoreEvent() {
    return false;
  }
  toDOM() {
    const chip = document.createElement("span");
    chip.className = "cm-code-lang";
    chip.textContent = this.lang;
    return chip;
  }
}

interface FenceLayout {
  /** The opening `CodeMark`, or null for a node that isn't really a fence. */
  open: SyntaxNode | null;
  /** The info string's language, `""` when the fence declared none. */
  lang: string;
  /** Line number of the closing fence; 0 for an unterminated block (still being
   *  typed). Nothing is folded there — assuming a closing line would eat a real
   *  line of code. */
  close: number;
  /** Fold the line break AFTER the opening fence, merging it into the first
   *  line of code. We do this when there is no chip to put on that line, so the
   *  band doesn't open with a blank row sitting inside its own tint. */
  swallowOpen: boolean;
  /** First / last document line that still HEADS a visible line box once the
   *  folds above are applied. This matters because CodeMirror gives a visual
   *  line spanning a folded break the decorations of its FIRST document line
   *  only — so a fold can move the band's END up, never its start, and putting
   *  `cm-codeblock-end` on the wrong line silently drops it. */
  firstVisible: number;
  lastVisible: number;
}

/** Everything both decoration layers need to know about one `FencedCode`. It
 *  lives in one function because `collect` (line classes) and `collectBlocks`
 *  (the replacements that do the folding) have to agree exactly: if they
 *  disagree by a line, the band's rounded corner and padding land on a line
 *  that is no longer a box of its own. */
function fenceLayout(doc: Text, node: SyntaxNode): FenceLayout {
  let open: SyntaxNode | null = null;
  let closeMark: SyntaxNode | null = null;
  let info: SyntaxNode | null = null;
  for (let c = node.firstChild; c; c = c.nextSibling) {
    if (c.name === "CodeMark") {
      if (!open) open = c;
      else closeMark = c;
    } else if (c.name === "CodeInfo") {
      info = c;
    }
  }
  const firstVisible = doc.lineAt(node.from).number;
  const lang = info ? doc.sliceString(info.from, info.to).trim() : "";
  const close = closeMark ? doc.lineAt(closeMark.from).number : 0;
  // Not when the closing fence is on the very next line: that range would
  // overlap the closing fold below, and an empty fence has no code to show.
  const swallowOpen = !lang && firstVisible < doc.lines && (!close || close > firstVisible + 1);
  // The closing fence folds into the line above it, so the band stops one line
  // short of it; if that is the line the opening fence just folded into, the
  // whole block collapses to a single visible box.
  let lastVisible = close > firstVisible ? close - 1 : doc.lineAt(node.to).number;
  if (swallowOpen && lastVisible === firstVisible + 1) lastVisible = firstVisible;
  return { open, lang, close, swallowOpen, firstVisible, lastVisible };
}

type Ranges = readonly { from: number; to: number }[];

/** Build the decoration set and the matching atomic-range set (the hidden
 *  markers + widgets, so cursor motion jumps over them). Pure over `state`, so
 *  it is unit-testable without a live `EditorView`.
 *
 *  Everything here is view-plugin-safe: inline marks, line classes, and
 *  replacements that stay WITHIN one line. Block-level work (whole tables, the
 *  collapsed code fences) lives in `collectBlocks` — CodeMirror rejects those
 *  from a plugin. */
function collect(
  state: EditorState,
  ranges: Ranges,
  focused: boolean,
): { decorations: DecorationSet; atomics: DecorationSet } {
  const doc = state.doc;
  const revealed = (from: number, to: number) => revealsAt(state, focused, from, to);
  const deco: Range<Decoration>[] = [];
  const atomic: Range<Decoration>[] = [];

  const mark = (from: number, to: number, cls: string) => {
    if (to > from) deco.push(Decoration.mark({ class: cls }).range(from, to));
  };
  const lineDeco = (pos: number, cls: string) => {
    deco.push(Decoration.line({ class: cls }).range(doc.lineAt(pos).from));
  };
  const replace = (from: number, to: number, spec: Parameters<typeof Decoration.replace>[0] = {}) => {
    if (to <= from) return;
    // A view plugin may not replace across a line break — CodeMirror throws
    // ("Decorations that replace line breaks may not be specified via plugins")
    // out of `EditorView.update`, taking the whole dispatch with it, so the
    // editor stops applying that change. Inline nodes really can span lines:
    // CommonMark lets an image's alt text or a wikilink's target wrap. Such a
    // node just stays raw rather than bringing the surface down. One guard here
    // covers every branch below, which is why none of them repeat it.
    if (doc.lineAt(from).number !== doc.lineAt(to).number) return;
    const d = Decoration.replace(spec);
    deco.push(d.range(from, to));
    atomic.push(d.range(from, to));
  };
  const hideMarks = (node: SyntaxNode) => {
    for (let c = node.firstChild; c; c = c.nextSibling) {
      if (/Mark$/.test(c.name)) replace(c.from, c.to);
    }
  };
  /** True when an inline span holds nothing but its own two markers — `****`,
   *  `====`, `~~~~`. Measured off the marker children rather than the node
   *  width so it holds for every marker length. */
  const isEmptyPair = (node: SyntaxNode) => {
    let open: SyntaxNode | null = null;
    let close: SyntaxNode | null = null;
    for (let c = node.firstChild; c; c = c.nextSibling) {
      if (!/Mark$/.test(c.name)) continue;
      if (!open) open = c;
      close = c;
    }
    return open != null && close != null && open !== close && open.to === close.from;
  };
  const eatSpace = (pos: number) => (doc.sliceString(pos, pos + 1) === " " ? pos + 1 : pos);

  const tree = syntaxTree(state);
  for (const { from, to } of ranges) {
    tree.iterate({
      from,
      to,
      enter: (ref) => {
        const name = ref.name;
        const nf = ref.from;
        const nt = ref.to;
        const touched = revealed(nf, nt);

        const cls = INLINE_CLASS[name];
        if (cls) {
          // An EMPTY pair (`****`, `====`, `~~~~`) keeps its markers visible and
          // takes no styling. Hiding them would erase the marker the moment it
          // was completed — the caret would sit in an apparently blank line with
          // four invisible characters around it, and typing the opening half of
          // a bold run would look like nothing happened at all.
          if (isEmptyPair(ref.node)) return;
          mark(nf, nt, cls);
          if (!touched) hideMarks(ref.node);
          return;
        }

        const h = HEADING_RE.exec(name);
        if (h) {
          lineDeco(nf, `cm-h${h[1]}`);
          if (!touched) {
            const m = ref.node.firstChild;
            if (m && m.name === "HeaderMark") replace(m.from, eatSpace(m.to));
          }
          return;
        }

        if (name === "Blockquote") {
          const first = doc.lineAt(nf).number;
          const last = doc.lineAt(nt).number;
          for (let l = first; l <= last; l++) lineDeco(doc.line(l).from, "cm-blockquote");
          if (!touched) {
            // Only the FIRST line of a multi-line blockquote paragraph gets an
            // explicit `QuoteMark` child node — lazy-continuation lines ("> "
            // on line 2+ of the same paragraph, no blank line between) keep
            // their "> " as literal text inside the Paragraph, not a separate
            // marker node. Scanning each line directly (instead of only the
            // node's direct children) hides the prefix on every line, not
            // just the first.
            //
            // Scanning from the NODE's start (not the line's) is what keeps a
            // nested quote honest: `> > b` is one line carrying two markers,
            // and the inner Blockquote begins mid-line. Anchored at the line
            // start it would match — and re-hide — its parent's marker, emit a
            // duplicate decoration, and leave its own "> " on screen.
            const markRe = /^ {0,3}>[ \t]?/;
            for (let l = first; l <= last; l++) {
              const line = doc.line(l);
              const start = Math.max(line.from, nf);
              const m = markRe.exec(doc.sliceString(start, line.to));
              if (m) replace(start, start + m[0].length);
            }
          }
          return;
        }

        if (name === "FencedCode") {
          const first = doc.lineAt(nf).number;
          // Touched, `collectBlocks` bails out and every source line of the
          // block is its own box; otherwise the folds it applies move the
          // band's last VISIBLE line — the one that gets the rounded bottom.
          const last = touched ? doc.lineAt(nt).number : fenceLayout(doc, ref.node).lastVisible;
          for (let l = first; l <= last; l++) lineDeco(doc.line(l).from, "cm-codeblock");
          lineDeco(doc.line(first).from, "cm-codeblock-start");
          lineDeco(doc.line(last).from, "cm-codeblock-end");
          return;
        }

        if (name === "Table") {
          // Rendered whole by `collectBlocks` unless the caret is inside it;
          // nothing under a block replacement can be seen, so skip the subtree.
          // Otherwise — caret inside, or a shape `collectBlocks` won't render
          // (see `tableBlock`) — fall through and decorate the raw pipes and
          // their inline markup as normal text.
          const block = tableBlock(doc, ref.node);
          if (block && !revealed(block.from, block.to)) return false;
          return;
        }

        if (name === "ListMark") {
          const ch = doc.sliceString(nf, nt);
          if (ch !== "-" && ch !== "*" && ch !== "+") {
            // Ordered item: keep the SOURCE number but show it in this level's
            // notation, so a nested list reads as nested instead of restarting
            // at "1." on every level.
            //
            // NOT gated on the caret, exactly like the bullet widget beside it.
            // A marker is not markup you edit through — the digits stay in the
            // document and only their notation is derived — so revealing it on
            // focus achieved nothing except hiding the level indicator at the
            // one moment it is most wanted: while you are typing that very line,
            // having just pressed Tab to create it.
            const om = /^(\d{1,9})([.)])$/.exec(ch);
            if (om) {
              const label = orderedLabel(
                Number(om[1]),
                depthOf(ref.node, "OrderedList"),
                om[2] ?? ".",
              );
              if (label !== ch) replace(nf, nt, { widget: new OrderedMarkWidget(label) });
            }
            return;
          }
          const restFrom = eatSpace(nt);
          const task = /^\[([ xX])\]/.exec(doc.sliceString(restFrom, restFrom + 3));
          if (!task) {
            replace(nf, restFrom, { widget: new BulletWidget(listDepth(ref.node)) });
            return;
          }
          // GFM task item: the whole `- [x] ` prefix becomes one real checkbox.
          // The done/strikethrough class stays on whether or not the caret is
          // here, so a finished item never flickers back to looking unfinished
          // while you edit its text.
          const line = doc.lineAt(nf);
          const checked = task[1] !== " ";
          if (checked) lineDeco(line.from, "cm-task-done");
          // Reveal on the LINE, not the marker: a caret anywhere on a task line
          // is editing that item, and a box that only unfolds when you hit its
          // three characters exactly is impossible to aim at.
          if (!revealed(line.from, line.to)) {
            replace(nf, eatSpace(restFrom + 3), { widget: new TaskWidget(checked, restFrom) });
          }
          return;
        }

        if (name === "Link") {
          mark(nf, nt, "cm-link");
          if (!touched) {
            for (let c = ref.node.firstChild; c; c = c.nextSibling) {
              if (c.name === "LinkMark" || c.name === "URL" || c.name === "LinkTitle") {
                replace(c.from, c.to);
              }
            }
          }
          return;
        }

        if (name === "Image") {
          if (!touched) {
            const m = /^!\[([^\]]*)\]\(([^)\s]+)/.exec(doc.sliceString(nf, nt));
            if (m) {
              const line = doc.lineAt(nf);
              const figure = line.text.trim() === doc.sliceString(nf, nt).trim();
              replace(nf, nt, { widget: new ImageWidget(m[2] ?? "", m[1] ?? "", figure) });
            }
          }
          return;
        }

        if (name === "WikiLink") {
          mark(nf, nt, "cm-wikilink");
          if (!touched) {
            for (let c = ref.node.firstChild; c; c = c.nextSibling) {
              if (c.name === "WikiLinkMark") replace(c.from, c.to);
            }
            // Hide the "target|" alias prefix, leaving just the alias visible.
            const innerFrom = nf + 2;
            const pipe = doc.sliceString(innerFrom, nt - 2).indexOf("|");
            if (pipe >= 0) replace(innerFrom, innerFrom + pipe + 1);
          }
          return;
        }

        if (name === "InlineMath") {
          if (!touched) {
            const tex = doc.sliceString(nf + 1, nt - 1);
            if (tex.trim()) replace(nf, nt, { widget: new MathWidget(tex) });
          }
          return;
        }

        if (name === "HorizontalRule") {
          if (!touched) replace(nf, nt, { widget: new HrWidget() });
          return;
        }
      },
    });
  }

  collectCitations(state, ranges, tree, focused, deco, atomic);

  return { decorations: Decoration.set(deco, true), atomics: RangeSet.of(atomic, true) };
}

/** `[node:id]` chips — plain text, not a tree node, so scan the visible text and
 *  skip any match sitting inside a code span/fence (parity with the renderer). */
function collectCitations(
  state: EditorState,
  ranges: Ranges,
  tree: Tree,
  focused: boolean,
  deco: Range<Decoration>[],
  atomic: Range<Decoration>[],
): void {
  for (const { from, to } of ranges) {
    const text = state.sliceDoc(from, to);
    CITATION_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = CITATION_RE.exec(text))) {
      const start = from + m.index;
      const end = start + m[0].length;
      if (revealsAt(state, focused, start, end)) continue;
      let inCode = false;
      for (let n: SyntaxNode | null = tree.resolveInner(start, 1); n; n = n.parent) {
        if (/Code/.test(n.name)) {
          inCode = true;
          break;
        }
      }
      if (inCode) continue;
      const d = Decoration.replace({ widget: new CitationWidget(m[1] ?? "") });
      deco.push(d.range(start, end));
      atomic.push(d.range(start, end));
    }
  }
}

/* ── Block layer ─────────────────────────────────────────────────────────────
 *  Rendered tables and collapsed code fences are BLOCK decorations (a table) or
 *  replacements that swallow a line break (the closing fence). CodeMirror
 *  refuses both from a view plugin — "Block decorations may not be specified
 *  via plugins" — because they change the editor's vertical layout, which is
 *  computed BEFORE plugin decorations are read. So they come from a state field
 *  instead, which costs us the viewport: this walks the whole tree. That is
 *  affordable here (it only ever does work at `Table`/`FencedCode` nodes, and
 *  notes are small) and it is the only shape CodeMirror accepts. */

/** Whether the editor has DOM focus, mirrored into state because `blockField`
 *  cannot see the view and still has to know (GAP 1 applies to blocks too — a
 *  note that OPENS with a table must render it, resting caret and all).
 *  `focusChangeEffect` lands this in the SAME transaction CodeMirror flags as
 *  `focusChanged`, so the field and the view plugin (which reads `view.hasFocus`
 *  directly) never disagree for even one frame. */
const setFocused = StateEffect.define<boolean>();

/** The block-level replacements, over the whole document. */
function collectBlocks(state: EditorState, focused: boolean): Range<Decoration>[] {
  const doc = state.doc;
  const out: Range<Decoration>[] = [];
  syntaxTree(state).iterate({
    enter: (ref) => {
      if (ref.name === "Table") {
        // Caret inside ⇒ leave the raw pipes alone so the table stays editable
        // in place. This is exactly Obsidian's behaviour. The reveal test runs
        // over the range we REPLACE (whole lines), not the node's own bounds —
        // see `tableBlock`.
        const block = tableBlock(doc, ref.node);
        if (block && !revealsAt(state, focused, block.from, block.to)) {
          const d = Decoration.replace({
            widget: new TableWidget(block.source, block.model, block.from),
            block: true,
          });
          out.push(d.range(block.from, block.to));
        }
        return false;
      }

      if (ref.name === "FencedCode") {
        if (revealsAt(state, focused, ref.from, ref.to)) return false;
        const { open, lang, close, swallowOpen, firstVisible } = fenceLayout(doc, ref.node);
        if (!open) return false;
        // Opening fence → just the language chip (or nothing at all, folded
        // away, when it declared no language). Replacing from the backticks
        // rather than the line start keeps any indentation of a fence nested in
        // a list item — but only while that line SURVIVES as its own line box.
        // When it is folded into the first line of code, its leading whitespace
        // would ride along and push that one line further right than the rest
        // of the block, so the fold has to start at the line instead.
        const openLine = doc.line(firstVisible);
        const openTo = swallowOpen ? doc.line(firstVisible + 1).from : openLine.to;
        const openFrom = swallowOpen ? openLine.from : open.from;
        out.push(Decoration.replace(lang ? { widget: new CodeLangWidget(lang) } : {}).range(openFrom, openTo));
        // Closing fence → gone. Swallowing the line break BEFORE it is what
        // removes the line box; replacing [line.from, line.to] on its own would
        // leave an empty line hanging under the block.
        if (close > firstVisible) {
          out.push(Decoration.replace({}).range(doc.line(close - 1).to, doc.line(close).to));
        }
        return false;
      }

      return undefined;
    },
  });
  return out;
}

interface BlockLayer {
  focused: boolean;
  decorations: DecorationSet;
  atomics: DecorationSet;
}

function blockLayer(state: EditorState, focused: boolean): BlockLayer {
  const ranges = collectBlocks(state, focused);
  return { focused, decorations: Decoration.set(ranges, true), atomics: RangeSet.of(ranges, true) };
}

const blockField = StateField.define<BlockLayer>({
  create: (state) => blockLayer(state, false),
  update(prev, tr) {
    let focused = prev.focused;
    for (const e of tr.effects) if (e.is(setFocused)) focused = e.value;
    // Same rebuild triggers as the view plugin, plus a syntax-tree swap: the
    // language parser finishes a chunk in the background and announces it with
    // a transaction that changes neither the document nor the selection.
    if (
      !tr.docChanged &&
      !tr.selection &&
      focused === prev.focused &&
      syntaxTree(tr.startState) === syntaxTree(tr.state)
    ) {
      return prev;
    }
    return blockLayer(tr.state, focused);
  },
  provide: (f) => [
    EditorView.decorations.from(f, (v) => v.decorations),
    EditorView.atomicRanges.of((view) => view.state.field(f, false)?.atomics ?? Decoration.none),
  ],
});

/** Test-friendly entry point: decorations for the whole document. */
export function buildDecorations(view: EditorView): DecorationSet {
  return collect(view.state, view.visibleRanges, view.hasFocus).decorations;
}

/** Inline/line decorations over the full document — used by unit tests (no live
 *  view). `focused` defaults to true, the state a test is usually describing. */
export function decorationsForState(state: EditorState, focused = true): DecorationSet {
  return collect(state, [{ from: 0, to: state.doc.length }], focused).decorations;
}

/** The block layer's decorations (tables, collapsed fences) — unit tests only. */
export function blockDecorationsForState(state: EditorState, focused = true): DecorationSet {
  return Decoration.set(collectBlocks(state, focused), true);
}

const plugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    atomics: DecorationSet;
    constructor(view: EditorView) {
      const r = collect(view.state, view.visibleRanges, view.hasFocus);
      this.decorations = r.decorations;
      this.atomics = r.atomics;
      // Live Preview can be switched on (compartment reconfigure) while the
      // editor already holds focus, and `focusChangeEffect` only fires on a
      // real focus/blur — seed the mirrored flag once so the block layer
      // doesn't spend the rest of the session believing we are unfocused. A
      // plugin may not dispatch from inside the update cycle: defer one
      // microtask, which still lands before the browser paints.
      if (view.hasFocus) {
        queueMicrotask(() => {
          if (view.dom.isConnected && view.state.field(blockField, false)?.focused === false) {
            view.dispatch({ effects: setFocused.of(view.hasFocus) });
          }
        });
      }
    }
    update(u: ViewUpdate) {
      if (
        u.docChanged ||
        u.selectionSet ||
        u.viewportChanged ||
        // Markup only reveals while the editor is focused, so a focus change
        // alone changes the whole layer.
        u.focusChanged ||
        syntaxTree(u.startState) !== syntaxTree(u.state)
      ) {
        const r = collect(u.view.state, u.view.visibleRanges, u.view.hasFocus);
        this.decorations = r.decorations;
        this.atomics = r.atomics;
      }
    }
  },
  {
    decorations: (v) => v.decorations,
    provide: (p) => EditorView.atomicRanges.of((view) => view.plugin(p)?.atomics ?? Decoration.none),
  },
);

/** Following a `[[wikilink]]` from the EDITOR. Live Preview styled them as links
 *  and then did nothing when one was clicked — live-looking and inert, in the
 *  mode the editor opens in. A click now goes where the read-only Preview's
 *  anchor goes, through the same resolver.
 *
 *  It only follows when the caret is NOT already inside the link: with the
 *  markers revealed you are editing the link text, and a click there has to
 *  place the caret like anywhere else (Obsidian draws the same line). Handled on
 *  `mousedown` so the editor never moves the selection first. */
const followWikiLink = EditorView.domEventHandlers({
  mousedown(event, view) {
    if (event.button !== 0) return false;
    const el = event.target as HTMLElement | null;
    const modified = event.metaKey || event.ctrlKey;

    // ── [[wikilink]] ──────────────────────────────────────────────────────
    const span = el?.closest?.(".cm-wikilink");
    if (span) {
      const pos = view.posAtDOM(span);
      const inner = syntaxTree(view.state).resolveInner(pos + 1, 1);
      const link = inner.name === "WikiLink" ? inner : inner.parent;
      if (!link || link.name !== "WikiLink") return false;
      // With the caret inside, the markers are revealed and you are EDITING the
      // link text, so a plain click has to place the caret like anywhere else.
      // ⌘/Ctrl-click follows regardless — that modifier means "go there", and
      // having it fail on the link you are looking at would be arbitrary.
      if (!modified && selectionTouches(view.state, link.from, link.to)) return false;
      // `[[Target|alias]]` resolves by TARGET, never by the alias shown.
      const target = view.state.sliceDoc(link.from + 2, link.to - 2).split("|")[0] ?? "";
      if (!target.trim()) return false;
      event.preventDefault();
      if (modified) void openWikiLinkInBackground(target);
      else void openWikiLink(target);
      return true;
    }

    // ── [text](url) ───────────────────────────────────────────────────────
    // These were styled as links and did nothing at all: live-looking and inert,
    // in the mode the editor opens in. They follow on ⌘/Ctrl-click (the
    // editor convention — a plain click still edits the text), or on a plain
    // click when the caret is elsewhere, which is how the wikilinks beside them
    // already behave.
    const anchor = el?.closest?.(".cm-link");
    if (!anchor) return false;
    const pos = view.posAtDOM(anchor);
    const inner = syntaxTree(view.state).resolveInner(pos + 1, 1);
    let link: typeof inner | null = inner;
    while (link && link.name !== "Link") link = link.parent;
    if (!link) return false;
    if (!modified && selectionTouches(view.state, link.from, link.to)) return false;
    const href = linkTarget(view.state.sliceDoc(link.from, link.to));
    if (!href) return false;
    event.preventDefault();
    openMarkdownHref(href, { newTab: modified });
    return true;
  },
});

/** The URL out of `[text](url "title")`. Returns null when there isn't one —
 * a reference-style link, or a half-typed one the writer is still in. */
function linkTarget(source: string): string | null {
  const match = /\]\(\s*<?([^\s<>)]+)>?(?:\s+["'(].*)?\s*\)$/.exec(source);
  return match?.[1] ?? null;
}

/** Where a markdown link goes.
 *
 * An in-app hash route opens INSIDE the app — routed in place, or as a
 * background panel for ⌘/Ctrl-click. Never a browser tab: that would boot a
 * second copy of the studio, lose this editor's unsaved buffer, and leave two
 * workspaces that know nothing about each other. Only a genuinely external URL
 * gets a browser tab, because that is the only thing a browser tab is for. */
function openMarkdownHref(href: string, { newTab }: { newTab: boolean }): void {
  if (href.startsWith("#/")) {
    const route = parseRoute(href);
    if (newTab && route?.docId) {
      if (route.docKind === "node") {
        workspace.openMarkdown(route.docId, "", { background: true });
      } else {
        workspace.openDoc(
          { id: route.docId, title: "", docKind: "document" },
          { background: true },
        );
      }
      return;
    }
    window.location.hash = href;
    return;
  }
  if (href.startsWith("#")) return; // an in-document anchor; nothing to navigate to
  window.open(href, "_blank", "noopener");
}

/** The Live Preview extension: inline decoration layer + atomic markers, plus
 *  the block layer (tables, collapsed fences) and the focus mirror both of them
 *  read. */
export const livePreview: Extension = [
  blockField,
  autoAlignTables,
  EditorView.focusChangeEffect.of((_state, focusing) => setFocused.of(focusing)),
  followWikiLink,
  plugin,
];

/* ── Indentation guides ──────────────────────────────────────────────────────
 *  Thin vertical rails descending through indented content (nested lists,
 *  indented blocks) — an editor-style indent guide per nesting level. Cheap and
 *  decoration-based: one line decoration per indented line whose inline
 *  `background` paints N 1px verticals (one per enclosing level). Each rail sits
 *  at its indent level's REAL x — the pixel width of that many leading spaces in
 *  the editor's current (proportional Inter, not monospace) font — so the rails
 *  line up under the bullets. Colour is the muted `--border` token, so it tracks
 *  the theme and stays quiet rather than garish. */

/** Hard cap on drawn guides per line — a pathologically indented line never
 *  balloons the inline style. */
const GUIDE_LEVEL_CAP = 16;

/** How wide ONE nesting level reads on screen, in `em` of the writing surface.
 *  A markdown indent level is two spaces, which in the proportional Inter face
 *  is barely 9px — legible as "something changed", not as "this is a child of
 *  that". The level is therefore STRETCHED to this width visually while the
 *  document keeps its canonical two spaces: nothing about the saved bytes, the
 *  vault `.md`, or how any other tool parses the note changes. */
const INDENT_STEP_EM = 1.9;

/** Cached font metrics keyed by the font shorthand they were measured for.
 *  Recomputed on font change (below) and on geometry change (font load / resize,
 *  which the plugin's `update` invalidates). The editor uses the proportional
 *  Inter face, so a space (indent unit) is NARROWER than a `ch` (digit advance);
 *  measuring the real space advance is what keeps the rails under the bullets. */
let cachedFontKey = "";
let cachedSpaceAdvance = 0;
let cachedFontSize = 0;
let measureCtx: CanvasRenderingContext2D | null = null;

/** Width in px of one space, and the font size, in `contentDOM`'s computed font.
 *  The advance is measured with a canvas so it needs no live layout; both are
 *  cached per font shorthand. */
function fontMetrics(contentDOM: HTMLElement): { advance: number; fontSize: number } {
  const cs = getComputedStyle(contentDOM);
  // Build the canvas font from resolved sub-properties: the `font` shorthand can
  // serialise empty when the family comes from a CSS custom property (--font-sans).
  const key = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  if (key === cachedFontKey && cachedSpaceAdvance > 0) {
    return { advance: cachedSpaceAdvance, fontSize: cachedFontSize };
  }
  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
  const fontSize = parseFloat(cs.fontSize) || 16;
  let px = 0;
  if (measureCtx) {
    measureCtx.font = key;
    px = measureCtx.measureText(" ").width;
  }
  // Fallback for headless/canvas-less environments (jsdom): a space is ~0.25em.
  if (!(px > 0)) px = fontSize * 0.25 || 4;
  cachedFontKey = key;
  cachedSpaceAdvance = px;
  cachedFontSize = fontSize;
  return { advance: px, fontSize };
}

/** Drop the cached measurement so the next paint re-measures (font load / theme
 *  swap / geometry change can move the space advance). */
function invalidateSpaceAdvance(): void {
  cachedFontKey = "";
  cachedSpaceAdvance = 0;
  cachedFontSize = 0;
}

/** Extra `letter-spacing` (px) to hang on each leading space so that `unit` of
 *  them occupy exactly `stepPx`. `letter-spacing` adds its value AFTER every
 *  character in the span — including the last — so n spaces measure
 *  `n × (advance + extra)`, and the level boundary lands on `stepPx` exactly.
 *  That exactness is the point: the guide rails are painted at multiples of
 *  `stepPx`, so any drift here would slide the rails off the bullets.
 *  Never negative — a font whose space is already wider than the target keeps
 *  its natural width rather than being squeezed. */
export function indentLetterSpacing(stepPx: number, unit: number, advance: number): number {
  return Math.max(0, stepPx / Math.max(1, unit) - advance);
}

/** Count of leading SPACE characters, or null when the indent contains a tab.
 *  Tabs are laid out by the browser against its own tab stops, so the
 *  letter-spacing arithmetic above would not hold for them — such a line keeps
 *  its natural indent (it still gets guides, just not the stretch). */
export function leadingSpaces(text: string): number | null {
  let n = 0;
  while (n < text.length && text[n] === " ") n++;
  return text[n] === "\t" ? null : n;
}

/** Visual column count of a line's leading whitespace (tabs expanded to stops). */
export function leadingColumns(text: string, tabSize: number): number {
  let col = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === " ") col += 1;
    else if (ch === "\t") col += tabSize - (col % tabSize);
    else break;
  }
  return col;
}

/** Inline `background` painting `levels` vertical guides at 0, step, 2·step … px
 *  from the line's content box, where `stepPx` is the real pixel width of ONE
 *  indent level (indent-unit spaces) in the current font. `background-origin:
 *  content-box` measures from inside the `.cm-line` padding, so the rails align
 *  with the leading whitespace / bullet columns regardless of that padding. */
function guideStyle(levels: number, stepPx: number): string {
  const layers: string[] = [];
  const positions: string[] = [];
  for (let k = 0; k < levels; k++) {
    layers.push("linear-gradient(hsl(var(--border)) 0 0)");
    positions.push(`${(k * stepPx).toFixed(2)}px 0`);
  }
  return (
    `background-image:${layers.join(",")};` +
    `background-position:${positions.join(",")};` +
    "background-size:1px 100%;" +
    "background-repeat:no-repeat;" +
    "background-origin:content-box"
  );
}

function buildGuides(view: EditorView): DecorationSet {
  const unit = Math.max(1, getIndentUnit(view.state));
  const tabSize = view.state.tabSize;
  const { advance, fontSize } = fontMetrics(view.contentDOM);
  // Pixel width of one indent level. The NATURAL width is indent-unit spaces at
  // the real (Inter) space advance — NOT `unit` ch, which over-shoots in a
  // proportional font — but two Inter spaces read as a hairline, so a level is
  // widened to INDENT_STEP_EM and the leading whitespace is stretched to match
  // (see `indentLetterSpacing`). Rails and text therefore move together.
  const stepPx = Math.max(unit * advance, INDENT_STEP_EM * fontSize);
  const extra = indentLetterSpacing(stepPx, unit, advance);
  const ranges: Range<Decoration>[] = [];
  for (const { from, to } of view.visibleRanges) {
    let pos = from;
    while (pos <= to) {
      const line = view.state.doc.lineAt(pos);
      if (line.text.trim()) {
        const levels = Math.min(GUIDE_LEVEL_CAP, Math.floor(leadingColumns(line.text, tabSize) / unit));
        if (levels > 0) {
          ranges.push(
            Decoration.line({
              class: "cm-indent-guides",
              attributes: { style: guideStyle(levels, stepPx) },
            }).range(line.from),
          );
        }
        // Stretch the indent itself. Applied off `levels > 0` so a one-space
        // hanging indent (below a level) is left exactly as typed.
        const spaces = levels > 0 && extra > 0 ? leadingSpaces(line.text) : null;
        if (spaces) {
          ranges.push(
            Decoration.mark({
              class: "cm-indent",
              attributes: { style: `letter-spacing:${extra.toFixed(2)}px` },
            }).range(line.from, line.from + spaces),
          );
        }
      }
      pos = line.to + 1;
    }
  }
  // Sorted on construction rather than via RangeSetBuilder: a line decoration
  // and this mark share a `from`, and the builder demands the caller get that
  // tie-break right.
  return Decoration.set(ranges, true);
}

const indentGuidePlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildGuides(view);
    }
    update(u: ViewUpdate) {
      // A geometry change (web-font load, resize, zoom) can move the space
      // advance — drop the cached measurement so the rails re-derive at the new
      // metrics. Rebuild on any of doc / viewport / geometry change.
      if (u.geometryChanged) invalidateSpaceAdvance();
      if (u.docChanged || u.viewportChanged || u.geometryChanged) this.decorations = buildGuides(u.view);
    }
  },
  { decorations: (v) => v.decorations },
);

/** Indentation guides for nested/indented markdown — always on (Live + Source). */
export const indentGuides: Extension = indentGuidePlugin;
